import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Price, Select, Tabs } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import {
  useTableauAssurance,
  usePolices,
  useAnnexeAssurance,
  messageErreurAssurance,
  type PolicePresentee,
  type BilanPolice,
} from '../../api/assurance.js';
import { enregistrerFichier, ouvrirUrlSignee } from '../../api/client.js';
import { useLieux } from '../../api/lieux.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ReevaluationLotDialog } from './ReevaluationLotDialog.js';
import { PoliceDialog } from './PoliceDialog.js';
import styles from './AssurancePage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function AssurancePage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie, compte } = useShellContext();
  const tableau = useTableauAssurance(galerieSlug);
  const polices = usePolices(galerieSlug);
  const lieux = useLieux(galerieSlug);
  const annexe = useAnnexeAssurance(galerieSlug);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const peutEcrire = galerieALaction(galerie, 'insurance:write');
  const [selection, setSelection] = useState<string[]>([]);
  const [lot, setLot] = useState(false);
  const [police, setPolice] = useState<PolicePresentee | null | undefined>(undefined);
  const [policeAnnexe, setPoliceAnnexe] = useState('');
  const [format, setFormat] = useState('pdf');
  const [erreur, setErreur] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);
  const oeuvresSelectionnees = [
    ...(tableau.data?.aReevaluer.map((l) => l.oeuvre) ?? []),
    ...(tableau.data?.sansValeur ?? []),
  ].filter((o) => selection.includes(o._id));
  function lienOeuvre(id: string, titre: string) {
    return <Link to={`/${galerieSlug}/oeuvres/${id}`}>{titre}</Link>;
  }
  function bilan(b: BilanPolice) {
    return (
      <article key={b.police._id} className={styles.carte}>
        <h2>{b.police.numero}</h2>
        <Price cents={b.totalCentimes} currency={b.police.devise} lang={langue} />
        {b.depassementGlobal && (
          <p className={styles.alerte}>{t('regie:assurance.depassementGlobal')}</p>
        )}
        {b.depassementsOeuvres.map((o) => (
          <p key={o.oeuvreId}>
            {t('regie:assurance.depassementOeuvre')} :{' '}
            {lienOeuvre(
              o.oeuvreId,
              b.oeuvresCouvertes.find((c) => c.oeuvre._id === o.oeuvreId)?.oeuvre.titre ??
                o.oeuvreId,
            )}{' '}
            — <Price cents={o.montantCentimes} currency={b.police.devise} lang={langue} />
          </p>
        ))}
        {b.devisesDifferentes.map((o) => (
          <p key={o.oeuvreId}>
            {t('regie:assurance.deviseDifferente')} :{' '}
            {lienOeuvre(
              o.oeuvreId,
              b.oeuvresCouvertes.find((c) => c.oeuvre._id === o.oeuvreId)?.oeuvre.titre ??
                o.oeuvreId,
            )}{' '}
            — <Price cents={o.montantCentimes} currency={o.devise} lang={langue} />
          </p>
        ))}
      </article>
    );
  }
  async function exporter() {
    setErreur('');
    setExportEnCours(true);
    try {
      const resultat = await annexe(format as 'pdf' | 'csv' | 'xlsx', {
        langue,
        ...(policeAnnexe ? { policeId: policeAnnexe } : { oeuvreIds: selection }),
      });
      if ('url' in resultat) ouvrirUrlSignee(resultat.url);
      else enregistrerFichier(resultat, `annexe-assurance.${format}`);
    } catch (e) {
      setErreur(messageErreurAssurance(e));
    } finally {
      setExportEnCours(false);
    }
  }
  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.titre}>{t('regie:assurance.titre')}</h1>
        {tableau.data && (
          <p>
            {t('regie:assurance.calculeLe', {
              date: formatDate(new Date(tableau.data.calculeLe), langue, 'court'),
            })}
          </p>
        )}
      </header>
      {tableau.isPending && <p role="status">{t('commun:chargement')}</p>}
      {tableau.isError && <p role="alert">{messageErreurAssurance(tableau.error)}</p>}
      {erreur && <p role="alert">{erreur}</p>}
      <div className={styles.actions}>
        {peutEcrire && (
          <Button
            disabled={!oeuvresSelectionnees.length || oeuvresSelectionnees.length > 500}
            onClick={() => setLot(true)}
          >
            {t('regie:assurance.reevaluer')}
          </Button>
        )}
        <Select
          aria-label={t('regie:assurance.porteeAnnexe')}
          value={policeAnnexe}
          onValueChange={setPoliceAnnexe}
          options={[
            { value: '', label: t('regie:assurance.selection') },
            ...(polices.data?.polices ?? []).map((b) => ({
              value: b.police._id,
              label: b.police.numero,
            })),
          ]}
        />
        <Select
          aria-label={t('regie:assurance.formatAnnexe')}
          value={format}
          onValueChange={setFormat}
          options={['pdf', 'csv', 'xlsx'].map((value) => ({ value, label: value.toUpperCase() }))}
        />
        <Button
          disabled={!policeAnnexe && (!selection.length || selection.length > 500)}
          loading={exportEnCours}
          onClick={() => void exporter()}
        >
          {t('regie:assurance.annexe')}
        </Button>
      </div>
      {tableau.data && (
        <Tabs
          defaultValue="aReevaluer"
          tabs={[
            {
              value: 'aReevaluer',
              label: t('regie:assurance.onglets.aReevaluer'),
              content: (
                <div className={styles.liste}>
                  {tableau.data.aReevaluer.length === 0 && <p>{t('regie:assurance.vide')}</p>}
                  {tableau.data.aReevaluer.map(({ oeuvre, raisons }) => (
                    <article key={oeuvre._id} className={styles.carte}>
                      <label className={styles.actions}>
                        <input
                          type="checkbox"
                          checked={selection.includes(oeuvre._id)}
                          onChange={(e) =>
                            setSelection(
                              e.target.checked
                                ? [...selection, oeuvre._id]
                                : selection.filter((id) => id !== oeuvre._id),
                            )
                          }
                        />
                        {oeuvre.titre}
                      </label>
                      {lienOeuvre(oeuvre._id, oeuvre.numeroInventaire)}
                      {raisons.map((r, rang) => (
                        <p key={rang}>
                          {r.type === 'periodicite' ? (
                            t('regie:assurance.raisons.periodicite')
                          ) : (
                            <>
                              {t('regie:assurance.raisons.vente_comparable')} :{' '}
                              {lienOeuvre(r.vente.oeuvreId, r.vente.oeuvreId)} —{' '}
                              <Price
                                cents={r.vente.prixCentimes}
                                currency={r.vente.devise}
                                lang={langue}
                              />{' '}
                              — {formatDate(new Date(r.vente.date), langue, 'court')}
                            </>
                          )}
                        </p>
                      ))}
                    </article>
                  ))}
                </div>
              ),
            },
            {
              value: 'sansValeur',
              label: t('regie:assurance.onglets.sansValeur'),
              content: (
                <div className={styles.liste}>
                  {tableau.data.sansValeur.length === 0 && <p>{t('regie:assurance.vide')}</p>}
                  {tableau.data.sansValeur.map((o) => (
                    <article key={o._id} className={styles.carte}>
                      <label className={styles.actions}>
                        <input
                          type="checkbox"
                          checked={selection.includes(o._id)}
                          onChange={(e) =>
                            setSelection(
                              e.target.checked
                                ? [...selection, o._id]
                                : selection.filter((id) => id !== o._id),
                            )
                          }
                        />
                        {o.titre}
                      </label>
                      {lienOeuvre(o._id, o.numeroInventaire)}
                    </article>
                  ))}
                </div>
              ),
            },
            {
              value: 'totaux',
              label: t('regie:assurance.onglets.totaux'),
              content: (
                <div className={styles.liste}>
                  <h2>{t('regie:assurance.parLieu')}</h2>
                  {tableau.data.totauxParLieu.map((l) => (
                    <p key={`${l.lieuId}:${l.devise}`}>
                      {lieux.data?.find((x) => x.id === l.lieuId)?.nom ??
                        t('regie:assurance.sansLieu')}{' '}
                      — <Price cents={l.totalCentimes} currency={l.devise} lang={langue} />
                    </p>
                  ))}
                  <h2>{t('regie:assurance.parPolice')}</h2>
                  {tableau.data.totauxParPolice.map(bilan)}
                </div>
              ),
            },
            {
              value: 'echeances',
              label: t('regie:assurance.onglets.echeances'),
              content: (
                <div className={styles.liste}>
                  {tableau.data.echeances.length === 0 && <p>{t('regie:assurance.vide')}</p>}
                  {tableau.data.echeances.map((e) => (
                    <p key={e.police._id}>
                      {e.police.numero} — {formatDate(new Date(e.police.fin), langue, 'court')} —{' '}
                      {t(`regie:assurance.jalons.${e.jalon}`)}
                    </p>
                  ))}
                </div>
              ),
            },
            {
              value: 'polices',
              label: t('regie:assurance.onglets.polices'),
              content: (
                <div className={styles.liste}>
                  <Verrouillable action="insurance:write" ouvert={peutEcrire}>
                    <Button onClick={() => setPolice(null)}>
                      {t('regie:assurance.creerPolice')}
                    </Button>
                  </Verrouillable>
                  {polices.isError && <p role="alert">{messageErreurAssurance(polices.error)}</p>}
                  {polices.data?.polices.map((b) => (
                    <div key={b.police._id}>
                      {bilan(b)}
                      <p>
                        {b.police.archivee
                          ? t('regie:assurance.archivee')
                          : `${formatDate(new Date(b.police.debut), langue, 'court')} — ${formatDate(new Date(b.police.fin), langue, 'court')}`}
                      </p>
                      <Verrouillable action="insurance:write" ouvert={peutEcrire}>
                        <Button variant="secondary" onClick={() => setPolice(b.police)}>
                          {t('regie:assurance.modifierPolice')}
                        </Button>
                      </Verrouillable>
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
      {lot && (
        <ReevaluationLotDialog
          galerieSlug={galerieSlug}
          compteId={compte.id}
          oeuvres={oeuvresSelectionnees}
          onFermer={() => {
            setLot(false);
            setSelection([]);
          }}
        />
      )}
      {police !== undefined && (
        <PoliceDialog
          galerieSlug={galerieSlug}
          police={police}
          onFermer={() => setPolice(undefined)}
          onRecharger={() => {
            setPolice(undefined);
            void polices.refetch();
          }}
        />
      )}
    </div>
  );
}
